// The one place the run comparison page reads API field names (PROG-66742 part 3,
// openspec/changes/evaluation-run-comparison). Field names follow the contract
// proposed for PROG-66951; when core renames one, change it here,
// not in the components.

export const GROUPS = [
  // "expected a match": these groups count only questions with an expected bucket,
  // unlike the brick and false positive counts in the measure table (part 4).
  { key: "brick", label: "Brick (expected a match)" },
  { key: "false_positive", label: "False positive (expected a match)" },
  { key: "true_positive", label: "True positive" },
  { key: "clarification", label: "Clarification" },
  { key: "no_match_expected", label: "No match expected" },
];
export const GROUP_KEYS = GROUPS.map((g) => g.key);

export const groupLabel = (key) => GROUPS.find((g) => g.key === key)?.label ?? null;

// Query fields shared by the table and the export (part 5). Unset filters are
// left out, per the repo rule.
export const comparisonParams = (view) => {
  const params = new URLSearchParams();
  if (view.b) {
    params.set("preset_a", view.a);
    params.set("preset_b", view.b);
    params.set("group_by", view.by);
    if (view.diff) params.set("only_disagreements", "true");
  }
  if (view.script) params.set("script", view.script);
  if (view.group !== "all") params.set("group", view.group);
  if (view.sort) {
    params.set("sort", view.sort);
    params.set("order", view.order || "asc");
  }
  return params;
};

// Two engine versions read the diff endpoint; one reads that batch's results.
export const comparisonRoute = ({ tag, view, batchId, pinned, page, pageSize }) => {
  const params = comparisonParams(view);
  params.set("limit", pageSize);
  params.set("offset", page * pageSize);
  const base = `/eval/tags/${encodeURIComponent(tag)}`;
  if (view.b) {
    if (pinned) {
      params.set("batch_id_a", pinned.a);
      params.set("batch_id_b", pinned.b);
    }
    return `${base}/diff?${params.toString()}`;
  }
  return `${base}/batches/${encodeURIComponent(batchId)}/results?${params.toString()}`;
};

// Export (part 5): every row that matches the view, built by core as CSV text.
// `pinned` is the table's batch pair, so the file matches the rows on screen.
export const exportRoute = ({ tag, view, batchId, pinned }) => {
  const params = comparisonParams(view);
  if (!view.b) params.set("batch_id", batchId);
  if (view.b && pinned) {
    params.set("batch_id_a", pinned.a);
    params.set("batch_id_b", pinned.b);
  }
  return `/eval/tags/${encodeURIComponent(tag)}/export?${params.toString()}`;
};

const versionCell = (side) => ({
  answer: side.rank1_meaning ?? null,
  outcome: side.outcome,
  group: side.group ?? null,
  score: side.score ?? null,
});

// Maps a diff or results page to the rows and counts the components use.
export const mapComparisonPage = (resp, view, offset = 0) => {
  const rows = (resp.items || []).map((item, i) => ({
    id: offset + i,
    script: item.script,
    line_order: item.line_order ?? null,
    question: item.question,
    expected_bucket: item.accepted ?? null,
    expected: item.expected ?? null,
    versions: view.b
      ? { [view.a]: versionCell(item.preset_a), [view.b]: versionCell(item.preset_b) }
      : { [view.a]: versionCell(item) },
  }));
  return {
    rows,
    total: resp.total ?? rows.length,
    // Group counts per engine version: `a` is production, `b` is test.
    counts: resp.counts_a ? { a: resp.counts_a, b: resp.counts_b } : resp.counts ? { a: resp.counts } : null,
    pinned: resp.batch_id_a ? { a: resp.batch_id_a, b: resp.batch_id_b } : null,
  };
};
